/**
 * @file Album Folder Tags related fragments.
 */

import gql from 'graphql-tag';

import { printProductSizeRoesGraphicNodeDetails } from './print-product';

export const AlbumFolderTagPrintProductConfigurationDetails = gql`
  fragment AlbumFolderTagPrintProductConfigurationDetails on AlbumFolderTagPrintProductItem {
    id
    print_product_configuration {
      name
      print_product {
        id
        name
      }
      print_product_size {
        print_size {
          id
          name
        }
        price {
          value
          currency
        }
        min_price {
          value
          currency
        }
        roes_info {
          graphic_nodes(slice: { limit: 99 }) {
            total
            records {
              ...printProductSizeRoesGraphicNodeDetails
            }
          }
        }
      }
      print_product_size_roes_graphic_node {
        ...printProductSizeRoesGraphicNodeDetails
      }
    }
  }
  ${printProductSizeRoesGraphicNodeDetails}
`;

export const AlbumFolderTagCustomPrintDetails = gql`
  fragment AlbumFolderTagCustomPrintDetails on IAlbumFolderTag {
    custom_print_album_folder_tags {
      total
      records {
        id
        custom_print {
          id
          name
        }
        album_folder_tag {
          ... on IRecord {
            id
          }
        }
        price {
          value
          currency
        }
      }
    }
  }
`;

export const IAlbumFolderTag = gql`
  fragment IAlbumFolderTag on IAlbumFolderTag {
    name
    type
    tag_template {
      ... on IRecord {
        id
        created_at
        updated_at
      }
      ... on ITagTemplate {
        name
        type
      }
    }
    is_pricing_tag
    is_matching_tag
    is_green_screen_tag
    digital_downloads_enabled
    digital_downloads_price {
      value
      currency
    }
    print_product_items {
      total
      records {
        id
        print_product {
          id
          name
        }
        print_product_size {
          id
          print_size {
            id
            name
          }
          price {
            value
            currency
          }
          min_price {
            value
            currency
          }
        }
        ...AlbumFolderTagPrintProductConfigurationDetails
        price {
          value
          currency
        }
      }
    }
    ...AlbumFolderTagCustomPrintDetails
  }
  ${AlbumFolderTagPrintProductConfigurationDetails}
  ${AlbumFolderTagCustomPrintDetails}
`;

export const UniformAlbumFolderTagLevelTeam = gql`
  fragment UniformAlbumFolderTagLevelTeam on UniformAlbumFolderTagLevelTeam {
    id
    name
    coach_name
    joined_identities_count
  }
`;

export const AlbumFolderTagLevelDetails = gql`
  fragment AlbumFolderTagLevelDetails on UniformAlbumFolderTagLevel {
    id
    name
    album_folder_tag {
      ... on IRecord {
        id
      }
    }
    teams(slice: { limit: 999 }) {
      records {
        ...UniformAlbumFolderTagLevelTeam
      }
    }
    jerseys(slice: { limit: 999 }) {
      records {
        id
        name
        colors
        venue
        team {
          ...UniformAlbumFolderTagLevelTeam
        }
      }
    }
  }
  ${UniformAlbumFolderTagLevelTeam}
`;

export const UniformAlbumFolderTag = gql`
  fragment UniformAlbumFolderTag on UniformAlbumFolderTag {
    id
    name
    type
    gender
    uniform_enabled
    uniform_numbered
    uniform_different_home_and_away
    text_matching_enabled
    levels(slice: { limit: 999 }) {
      records {
        id
        ...AlbumFolderTagLevelDetails
      }
    }
  }
  ${AlbumFolderTagLevelDetails}
`;

export const AlbumFolderTagDetails = gql`
  fragment AlbumFolderTagDetails on IAlbumFolderTag {
    ... on IRecord {
      id
    }
    ... on IAlbumFolderTag {
      ...IAlbumFolderTag
    }
    ... on UniformAlbumFolderTag {
      ...UniformAlbumFolderTag
    }
  }
  ${IAlbumFolderTag}
  ${UniformAlbumFolderTag}
`;

export const AlbumAlbumFolderTagDetails = gql`
  fragment AlbumAlbumFolderTagDetails on AlbumAlbumFolderTag {
    id
    album_folder_tag {
      ...AlbumFolderTagDetails
    }
    uniform_album_folder_tag_levels(slice: { limit: 999 }) {
      records {
        id
        uniform_album_folder_tag_level {
          ...AlbumFolderTagLevelDetails
        }
        uniform_album_folder_tag_level_jerseys(slice: { limit: 999 }) {
          records {
            id
            name
            colors
            venue
            team {
              ...UniformAlbumFolderTagLevelTeam
            }
          }
        }
      }
    }
  }
  ${AlbumFolderTagDetails}
  ${AlbumFolderTagLevelDetails}
  ${UniformAlbumFolderTagLevelTeam}
`;

export const AlbumPhotoAlbumFolderTagDetails = gql`
  fragment AlbumPhotoAlbumFolderTagDetails on AlbumPhotoAlbumFolderTag {
    id
    album_folder_tag {
      ...AlbumFolderTagDetails
    }
    teams(slice: { limit: 999 }) {
      total
      records {
        id
        team {
          ...UniformAlbumFolderTagLevelTeam
        }
      }
    }
  }
  ${AlbumFolderTagDetails}
  ${UniformAlbumFolderTagLevelTeam}
`;

export const AlbumFolderPhotoSearchDetails = gql`
  fragment AlbumFolderPhotoSearchDetails on AlbumFolderPhotoSearch {
    id
    name
    album_folder_tags_filter_mode
    tags(slice: { limit: 999 }) {
      total
      records {
        album_folder_tag {
          ...AlbumFolderTagDetails
        }
      }
    }
  }
  ${AlbumFolderTagDetails}
`;

export const AlbumPhotoSearchDetails = gql`
  fragment AlbumPhotoSearchDetails on AlbumPhotoSearch {
    id
    name
    album_folder_tags_filter_mode
    tags(slice: { limit: 999 }) {
      total
      records {
        album_folder_tag {
          ...AlbumFolderTagDetails
        }
      }
    }
  }
  ${AlbumFolderTagDetails}
`;
